import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Image } from '@codes/ui-kit/components/ui/image';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@codes/ui-kit/components/ui/tooltip';
import { useMyRole } from '@web/app/shared/hooks';
import { useSelectedStore } from '@web/app/shared/stores';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { CodesLogoImage, LogoMiniDarkImage } from '@codes/assets';
import { cn } from '@codes/ui-kit';
import { useWorkspace } from '@codes/workspace';

import { MenuItem, getLightMenuItems, getMenuItems } from '../../consts';

const SideBarMenuItem = ({
    item,
    index,
    isHovered,
    isActive,
    isExpanded,
    showSubmenu,
    onToggleSubmenu,
    onNavigate,
    isSubItemActive,
    t,
}: {
    item: MenuItem;
    index: number;
    isHovered: boolean;
    isActive: boolean;
    isExpanded: boolean;
    showSubmenu: boolean;
    onToggleSubmenu: (index: number) => void;
    onNavigate: (href: string) => void;
    isSubItemActive: (href: string) => boolean;
    t: (key: string) => string;
}) => {
    const menuButton = (
        <div
            className={cn(
                'group relative flex items-center gap-3 px-2.5 cursor-pointer h-9',
                'rounded-lg transition-all duration-150',
                'hover:bg-hover/80',
                isActive && 'bg-accent text-point',
                showSubmenu && 'rounded-b-none bg-hover/60'
            )}
            onClick={() => {
                if (item.hasDropdown) {
                    onToggleSubmenu(index);
                } else if (item.href) {
                    onNavigate(item.href);
                }
            }}
        >
            {/* Active indicator — refined thin accent bar */}
            {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2.5px] h-4 bg-point rounded-r-full transition-all duration-200" />
            )}

            <div
                className={cn(
                    'shrink-0 transition-opacity duration-150',
                    isActive ? 'opacity-100' : 'opacity-40 group-hover:opacity-70'
                )}
            >
                <item.icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
            </div>

            <div
                className={cn(
                    'flex items-center flex-1 overflow-hidden transition-all duration-200 ease-out',
                    !isHovered ? 'w-0 opacity-0' : 'w-auto opacity-100'
                )}
            >
                <span
                    className={cn(
                        'flex-1 text-[13px] font-medium whitespace-nowrap tracking-tight',
                        isActive ? 'text-point' : 'text-text-600 group-hover:text-text'
                    )}
                >
                    {t(item.label)}
                </span>
                {item.hasDropdown && (
                    <div className="transition-transform duration-200 ease-in-out opacity-30">
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div>
            {/* Show tooltip only when collapsed (not hovered) */}
            {!isHovered ? (
                <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>{menuButton}</TooltipTrigger>
                    <TooltipContent side="right" sideOffset={8} className="text-xs font-medium">
                        {t(item.label)}
                    </TooltipContent>
                </Tooltip>
            ) : (
                menuButton
            )}

            {/* Submenu */}
            {showSubmenu && (
                <div className="bg-hover/40 rounded-b-lg py-1.5 px-3 space-y-0.5 animate-in fade-in slide-in-from-top-1 duration-150">
                    {item.subItems?.map((subItem, subIndex) => (
                        <div
                            key={subIndex}
                            className="cursor-pointer py-1.5 px-2.5 rounded-md hover:bg-hover/60 transition-colors duration-100"
                            onClick={() => onNavigate(subItem?.href)}
                        >
                            <span
                                className={cn(
                                    'text-[12px] font-medium transition-colors duration-150',
                                    isSubItemActive(subItem.href) ? 'text-point' : 'text-text-400 hover:text-text-600'
                                )}
                            >
                                {t(subItem.label)}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const SideBar = () => {
    const { workspace, project } = useSelectedStore();
    const { data: workspaceFromServer } = useWorkspace('0');

    const [expandedMenus, setExpandedMenus] = useState<Record<number, boolean>>({});
    const [isHovered, setIsHovered] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const myRole = useMyRole();

    const toggleSubmenu = (index: number) => {
        setExpandedMenus(prev => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    const isMenuItemActive = (item: MenuItem) => {
        if (item.hasDropdown && item.subItems) {
            return item.subItems.some(subItem => location.pathname === subItem.href.split('?')[0]);
        }
        return location.pathname === item.href;
    };

    const isSubItemActive = (href: string) => {
        return location.pathname === href.split('?')[0];
    };

    const items = useMemo(() => {
        const menuItems = !project
            ? getLightMenuItems(workspaceFromServer?.id)
            : getMenuItems(workspaceFromServer?.id, project?.id);

        if (myRole > 1) {
            return menuItems.filter(item => !item.label.includes('settings'));
        }

        return menuItems;
    }, [workspaceFromServer?.id, project, myRole]);

    const handleMouseLeave = () => {
        setIsHovered(false);
        setExpandedMenus({});
    };

    if (!workspace) {
        return null;
    }

    return (
        <TooltipProvider>
            <div
                className={cn(
                    'fixed top-0 left-0 bottom-0 z-[100]',
                    'bg-card/95 backdrop-blur-md border-r border-border/50',
                    'py-4 transition-all duration-250 ease-out',
                    'w-[60px] hover:w-[220px]',
                    'px-2 flex flex-col max-md:hidden'
                )}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={handleMouseLeave}
            >
                {/* Logo area */}
                <div className="relative h-[28px] mb-2 flex items-center justify-center">
                    <Image
                        className={cn(
                            'absolute h-[22px] object-contain transition-all duration-250',
                            isHovered ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
                        )}
                        src={LogoMiniDarkImage}
                    />
                    <Image
                        className={cn(
                            'absolute left-2 h-[22px] object-contain transition-all duration-250',
                            isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                        )}
                        src={CodesLogoImage}
                    />
                </div>

                {/* Divider */}
                <div className="h-px bg-border/40 mx-1 mb-2" />

                {/* Menu Items */}
                <div className="flex-1 flex flex-col gap-0.5 overflow-y-auto overflow-x-hidden scrollbar-hide">
                    {items.map((item, index) => {
                        const isActive = isMenuItemActive(item);
                        const isExpanded = !!expandedMenus[index];
                        const showSubmenu = item.hasDropdown && isExpanded && isHovered;

                        return (
                            <SideBarMenuItem
                                key={index}
                                item={item}
                                index={index}
                                isHovered={isHovered}
                                isActive={isActive}
                                isExpanded={isExpanded}
                                showSubmenu={showSubmenu}
                                onToggleSubmenu={toggleSubmenu}
                                onNavigate={navigate}
                                isSubItemActive={isSubItemActive}
                                t={t}
                            />
                        );
                    })}
                </div>
            </div>
        </TooltipProvider>
    );
};
